import { Alert, Button } from "antd";
import LineSpacer from "../../../../../../../components/LineSpacer";
import { PlusOutlined } from "@ant-design/icons";
import InitDeduction from "./InitDeduction";
import { WplywContext } from "../../../../../../../context/wplywContext";
import { useContext, useState } from "react";
import { useAuth } from "../../../../../../../context/AuthContext";
import Reckonings from "./Reckonings";
import ProgressBarReckoning from "./ProgressBarReckoning";
import formatPrice from "../../../../../../../components/formatPrice";
import axios from "../../../../../../../context/axiosConfig";

const InnerStage1 = (props) => {
	const { selectedPerson } = props;
	const { wplyw, setWplyw } = useContext(WplywContext);
	const { user } = useAuth();
	const isGeneratedTransferLocked =
		Boolean(selectedPerson?.msr_persons_transfers_generated_file) ||
		Boolean(selectedPerson?.msr_reserves_generated_file) ||
		Boolean(
			(selectedPerson?.msr_transfers || []).some(
				(t) => t?.msr_transfers_generated_file
			)
		) ||
		Boolean(
			(selectedPerson?.msr_reckonings || []).some((r) =>
				(r?.msr_transfers || []).some(
					(t) => t?.msr_transfers_generated_file
				)
			)
		);
	const isDisabled = wplyw.mode == "preview" || isGeneratedTransferLocked;
	const [creatingReckoning, setCreatingReckoning] = useState(false);
	const contractId =
		wplyw?.wplyw_data?.data?.msr_wplywy?.msr_wplywy_mso_contract_id;

	// Add new reckoning
	const addReckoning = async () => {
		if (creatingReckoning || isDisabled) {
			return;
		}

		setCreatingReckoning(true);

		let specialCommission = null;

		if (contractId) {
			try {
				const response = await axios.get(
					`/wplyw/mso/contracts/${contractId}/special-commission`
				);
				if (response?.data?.status) {
					specialCommission =
						response.data?.data?.specialCommission || null;
				}
			} catch (error) {
				// Nie blokujemy dodawania rozliczenia, logujemy błąd do konsoli
				console.error(
					"Błąd pobierania prowizji specjalnej z MSO:",
					error
				);
			}
		}

		try {
			setWplyw((prevData) => {
				const prevWplywData = prevData?.wplyw_data?.data?.msr_wplywy;
				const defaultValues =
					prevData?.wplyw_data?.data?.default_values || {};
				const defaultItems = defaultValues?.msr_reckonings_items || [];
				const defaultTypes = defaultValues?.msr_reckonings_types || [];

				if (
					!prevWplywData ||
					defaultItems.length === 0 ||
					defaultTypes.length === 0
				) {
					return prevData;
				}

				const persons = prevWplywData.msr_persons || [];
				const personIndex = persons.findIndex(
					(person) =>
						person.msr_persons_id === selectedPerson.msr_persons_id
				);

				if (personIndex === -1) {
					return prevData;
				}

				const prevPerson = persons[personIndex];
				const timestamp = Math.floor(new Date().getTime() / 1000);
				const wplywOwnId = prevWplywData.msr_wplywy_own_id;
				const changes = [...(prevData?.changes || [])];

				const normalizeBoolean = (value) =>
					value === true ||
					value === 1 ||
					value === "1" ||
					value === "true";

				const pushChange = ({
					etap,
					field,
					label,
					oldValue,
					newValue,
					objectid,
				}) => {
					const changeEntry = {
						etap,
						field,
						label,
						action: "edit",
						oldValue,
						newValue,
						datetime: timestamp,
						user: user.user.mlg_users_id,
						objectid,
						wplywid: wplywOwnId,
						personid: prevPerson.msr_persons_own_id,
					};

					const existingIndex = changes.findIndex(
						(change) =>
							change.field === field &&
							change.objectid === objectid
					);

					if (existingIndex > -1) {
						changes[existingIndex] = changeEntry;
					} else {
						changes.push(changeEntry);
					}
				};

				const parseNumber = (value) => {
					const numeric = Number(value);
					return Number.isFinite(numeric) ? numeric : 0;
				};

				const baseItem = defaultItems[0];
				const defaultTypeValue = defaultTypes[0]?.value;

				if (
					!baseItem ||
					defaultTypeValue === undefined ||
					defaultTypeValue === null
				) {
					return prevData;
				}

				let commissionOption = null;
				let resolvedCommissionValue = null;
				let allowsDefault = false;

				if (specialCommission) {
					commissionOption =
						defaultItems.find(
							(item) =>
								String(item.value) ===
								String(specialCommission.id)
						) || null;

					if (commissionOption) {
						const remoteAccess = normalizeBoolean(
							specialCommission.accessToDefaultPercent
						);
						const optionAccess = normalizeBoolean(
							commissionOption.msr_reckonings_items_access_to_default
						);

						allowsDefault = remoteAccess || optionAccess;

						if (
							specialCommission.defaultPercent !== null &&
							specialCommission.defaultPercent !== undefined &&
							Number.isFinite(
								Number(specialCommission.defaultPercent)
							)
						) {
							resolvedCommissionValue = Number(
								specialCommission.defaultPercent
							);
						}
					}
				}

				let updatedPersonCommission =
					prevPerson?.msr_persons_commission !== undefined
						? prevPerson.msr_persons_commission
						: null;

				if (
					commissionOption &&
					allowsDefault &&
					resolvedCommissionValue !== null
				) {
					const numericValue = Number(resolvedCommissionValue);
					const previousValue =
						prevPerson?.msr_persons_commission !== undefined
							? prevPerson.msr_persons_commission
							: null;

					if (Number(previousValue) !== numericValue) {
						pushChange({
							etap: "Osoba",
							field: "msr_persons_commission",
							label: "Prowizja",
							oldValue: previousValue,
							newValue: numericValue,
							objectid: prevPerson.msr_persons_own_id,
						});
					}

					updatedPersonCommission = numericValue;
				}

				const computePercentForSpecial = () => {
					if (!commissionOption) {
						return 0;
					}

					if (allowsDefault && resolvedCommissionValue !== null) {
						return resolvedCommissionValue;
					}

					return parseNumber(
						commissionOption.msr_reckonings_items_default_percent
					);
				};

				const updatedReckonings = (
					prevPerson?.msr_reckonings || []
				).map((reckoning) => {
					if (!commissionOption) {
						return reckoning;
					}

					const partAmount = parseNumber(
						reckoning?.msr_reckonings_part_amount
					);
					const percentSource = computePercentForSpecial();
					const deduction = Number(
						((percentSource / 100) * partAmount).toFixed(2)
					);
					const newItemValue = commissionOption.value;

					if (reckoning.msr_reckonings_items_id !== newItemValue) {
						pushChange({
							etap: "Rozliczenie",
							field: "msr_reckonings_items_id",
							label: "Przedmiot kwoty wpływu",
							oldValue: reckoning.msr_reckonings_items_id,
							newValue: newItemValue,
							objectid: reckoning.msr_reckonings_own_id,
						});
					}

					if (
						Number(
							reckoning.msr_reckonings_deduction_from_income
						) !== deduction
					) {
						pushChange({
							etap: "Rozliczenie",
							field: "msr_reckonings_deduction_from_income",
							label: "Rozliczenie od wpływu",
							oldValue:
								reckoning.msr_reckonings_deduction_from_income,
							newValue: deduction,
							objectid: reckoning.msr_reckonings_own_id,
						});
					}

					return {
						...reckoning,
						msr_reckonings_items_id: newItemValue,
						msr_reckonings_deduction_from_income: deduction,
					};
				});

				const sumPartAmounts = updatedReckonings.reduce(
					(sum, reck) =>
						sum + parseNumber(reck?.msr_reckonings_part_amount),
					0
				);

				let newPartAmount =
					updatedReckonings.length > 0
						? parseNumber(prevPerson.msr_persons_amount) -
						  sumPartAmounts
						: parseNumber(prevPerson.msr_persons_amount);

				if (newPartAmount < 0) {
					newPartAmount = 0;
				}

				newPartAmount = Number(newPartAmount.toFixed(2));

				const targetItem = commissionOption || baseItem;
				const targetItemAllowsDefault = normalizeBoolean(
					targetItem.msr_reckonings_items_access_to_default
				);
				const canUsePersonCommission =
					updatedPersonCommission !== null &&
					updatedPersonCommission !== undefined &&
					updatedPersonCommission !== "" &&
					Number.isFinite(Number(updatedPersonCommission));
				const personCommissionNumeric = canUsePersonCommission
					? Number(updatedPersonCommission)
					: null;
				const fallbackPercent = parseNumber(
					targetItem.msr_reckonings_items_default_percent
				);

				const percentForNew =
					targetItemAllowsDefault &&
					canUsePersonCommission &&
					personCommissionNumeric !== null
						? personCommissionNumeric
						: fallbackPercent;

				const deductionForNew = Number(
					((percentForNew / 100) * newPartAmount).toFixed(2)
				);

				const newReckoning = {
					msr_reckonings_id: "temp_" + Date.now(),
					msr_reckonings_own_id: `${Math.floor(
						new Date().getTime() / 1000
					)}`,
					msr_reckonings_part_amount: newPartAmount,
					msr_reckonings_deduction_from_income: deductionForNew,
					msr_reckonings_items_id: targetItem.value,
					msr_reckonings_types_id: defaultTypeValue,
					msr_reckonings_document: "",
					msr_reckonings_comment: "",
					msr_deductions: [],
					msr_transfers: [],
					msr_reckonings_user: prevPerson.msr_persons_user,
				};

				changes.push({
					etap: "Rozliczenia",
					field: "msr_reckonings",
					label: "Rozliczenia",
					action: "add",
					oldValue: null,
					newValue: `Dodano nowe rozliczenie o ID ${newReckoning.msr_reckonings_own_id}`,
					datetime: timestamp,
					user: user.user.mlg_users_id,
					objectid: newReckoning.msr_reckonings_own_id,
					wplywid: wplywOwnId,
					personid: prevPerson.msr_persons_own_id,
				});

				const updatedPerson = {
					...prevPerson,
					msr_persons_commission: updatedPersonCommission,
					msr_reckonings: [...updatedReckonings, newReckoning],
				};

				const updatedPersons = [...persons];
				updatedPersons[personIndex] = updatedPerson;

				return {
					...prevData,
					wplyw_data: {
						...prevData.wplyw_data,
						data: {
							...prevData.wplyw_data.data,
							msr_wplywy: {
								...prevData.wplyw_data.data.msr_wplywy,
								msr_persons: updatedPersons,
							},
						},
					},
					changes,
				};
			});
		} finally {
			setCreatingReckoning(false);
		}
	};

	return (
		<>
			{isGeneratedTransferLocked ? (
				<Alert
					message="Edycja zablokowana"
					description="Nie można edytować etapu 'Kwoty', ponieważ przelewy dla tej osoby zostały już wygenerowane."
					type="success"
					showIcon
					style={{ marginBottom: 16 }}
				/>
			) : null}
			{selectedPerson && (
				<div
					style={{
						padding: "16px",
						borderRadius: "8px",
						marginTop: "16px",
						background: "white",
						color: "black",
						border: "2px solid #BD8136",
						display: "grid",
						gridTemplateColumns: "repeat(6, 1fr)",
						gap: "20px",
					}}
				>
					<div>
						<div>Imię i nazwisko</div>
						<div
							style={{
								fontSize: "16px",
								marginTop: "8px",
								fontWeight: "700",
							}}
						>
							{selectedPerson?.msr_persons_name || "Brak danych"}{" "}
							{selectedPerson?.msr_persons_lastname || ""}
						</div>
					</div>
					<div>
						<div>Kwota do rozliczenia</div>
						<div
							style={{
								fontSize: "16px",
								marginTop: "8px",
								fontWeight: "700",
							}}
						>
							{formatPrice(
								selectedPerson?.msr_persons_amount || 0
							)}{" "}
							zł
						</div>
					</div>
					<div>
						<div>Suma prowizji</div>
						<div
							style={{
								fontSize: "16px",
								marginTop: "8px",
								fontWeight: "700",
							}}
						>
							{formatPrice(
								selectedPerson?.msr_reckonings?.reduce(
									(sum, reckonings) =>
										sum +
										parseFloat(
											reckonings?.msr_reckonings_deduction_from_income ||
												0
										),
									0
								) || 0
							)}{" "}
							zł [
							{(
								((selectedPerson?.msr_reckonings?.reduce(
									(sum, reckonings) =>
										sum +
										parseFloat(
											reckonings?.msr_reckonings_deduction_from_income ||
												0
										),
									0
								) || 0) /
									(selectedPerson?.msr_persons_amount || 1)) *
								100
							).toFixed(2)}
							%]
						</div>
					</div>
					<div>
						<div>Suma odliczeń</div>
						<div
							style={{
								fontSize: "16px",
								marginTop: "8px",
								fontWeight: "700",
							}}
						>
							{formatPrice(
								selectedPerson?.msr_reckonings?.reduce(
									(sum, reckonings) =>
										sum +
										parseFloat(
											reckonings?.msr_deductions?.reduce(
												(sum2, deductions) =>
													sum2 +
													parseFloat(
														deductions?.msr_deductions_amount ||
															0
													),
												0
											) || 0
										),
									0
								)
							)}{" "}
							zł [
							{(
								(selectedPerson?.msr_reckonings?.reduce(
									(sum, reckonings) =>
										sum +
										parseFloat(
											reckonings?.msr_deductions?.reduce(
												(sum2, deductions) =>
													sum2 +
													parseFloat(
														deductions?.msr_deductions_amount ||
															0
													),
												0
											) || 0
										),
									0
								) /
									(selectedPerson?.msr_persons_amount || 1)) *
								100
							).toFixed(2)}
							%]
						</div>
					</div>
					<div>
						<div>Prowizje + odliczenia</div>
						<div
							style={{
								fontSize: "16px",
								marginTop: "8px",
								fontWeight: "700",
							}}
						>
							{formatPrice(
								(selectedPerson?.msr_reckonings?.reduce(
									(sum, reckonings) =>
										sum +
										parseFloat(
											reckonings?.msr_reckonings_deduction_from_income ||
												0
										),
									0
								) || 0) +
									selectedPerson?.msr_reckonings?.reduce(
										(sum, reckonings) =>
											sum +
											parseFloat(
												reckonings?.msr_deductions?.reduce(
													(sum2, deductions) =>
														sum2 +
														parseFloat(
															deductions?.msr_deductions_amount ||
																0
														),
													0
												) || 0
											),
										0
									)
							)}{" "}
							zł [
							{(
								(((selectedPerson?.msr_reckonings?.reduce(
									(sum, reckonings) =>
										sum +
										parseFloat(
											reckonings?.msr_reckonings_deduction_from_income ||
												0
										),
									0
								) || 0) +
									selectedPerson?.msr_reckonings?.reduce(
										(sum, reckonings) =>
											sum +
											parseFloat(
												reckonings?.msr_deductions?.reduce(
													(sum2, deductions) =>
														sum2 +
														parseFloat(
															deductions?.msr_deductions_amount ||
																0
														),
													0
												) || 0
											),
										0
									)) /
									(selectedPerson?.msr_persons_amount || 1)) *
								100
							).toFixed(2)}
							%]
						</div>
					</div>
					<div>
						<div>Kwota dla klienta</div>
						<div
							style={{
								fontSize: "16px",
								marginTop: "8px",
								fontWeight: "700",
							}}
						>
							{formatPrice(
								(selectedPerson?.msr_persons_amount || 0) -
									((selectedPerson?.msr_reckonings?.reduce(
										(sum, reckonings) =>
											sum +
											parseFloat(
												reckonings?.msr_reckonings_deduction_from_income ||
													0
											),
										0
									) || 0) +
										selectedPerson?.msr_reckonings?.reduce(
											(sum, reckonings) =>
												sum +
												parseFloat(
													reckonings?.msr_deductions?.reduce(
														(sum2, deductions) =>
															sum2 +
															parseFloat(
																deductions?.msr_deductions_amount ||
																	0
															),
														0
													) || 0
												),
											0
										))
							)}{" "}
							zł
						</div>
					</div>
				</div>
			)}
			<ProgressBarReckoning
				personid={selectedPerson.msr_persons_own_id}
			></ProgressBarReckoning>
			{!isDisabled && (
				<div
					style={{
						gap: "20px",
						alignItems: "center",
						display: "flex",
						paddingTop: "16px",
						background: "white",
						paddingBottom: "10px",
						position: "sticky",
						top: "85px",
					}}
				>
					<LineSpacer />
					<Button
						loading={creatingReckoning}
						disabled={
							creatingReckoning ||
							selectedPerson.msr_persons_amount <=
								(selectedPerson.msr_reckonings || []).reduce(
									(sum, reck) =>
										sum +
										parseFloat(
											reck?.msr_reckonings_part_amount
										),
									0
								)
						}
						type="primary"
						style={{ fontSize: "16px", height: "auto" }}
						icon={<PlusOutlined />}
						onClick={addReckoning} // Trigger addReckoning on click
					>
						Podziel kwotę
					</Button>
				</div>
			)}
			<InitDeduction selectedPerson={selectedPerson}></InitDeduction>
			{selectedPerson?.msr_reckonings?.length > 0 &&
				selectedPerson.msr_reckonings.map((x, i) => (
					<Reckonings
						reckoning={x}
						selectedPerson={selectedPerson}
						index={i}
						key={x.msr_reckonings_id}
					/>
				))}
			{!isDisabled && (
				<div
					style={{
						gap: "20px",
						alignItems: "center",
						display: "flex",
						marginTop: "16px",
					}}
				>
					<LineSpacer />
					<Button
						loading={creatingReckoning}
						disabled={
							creatingReckoning ||
							selectedPerson.msr_persons_amount <=
								(selectedPerson.msr_reckonings || []).reduce(
									(sum, reck) =>
										sum +
										parseFloat(
											reck?.msr_reckonings_part_amount
										),
									0
								)
						}
						type="primary"
						style={{ fontSize: "16px", height: "auto" }}
						icon={<PlusOutlined />}
						onClick={addReckoning} // Trigger addReckoning on click
					>
						Podziel kwotę
					</Button>
				</div>
			)}
		</>
	);
};

export default InnerStage1;
